import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { evisortBlue } from '~/assets/shared-styles/general';
import EcInput from '~/components/Shared/EcInput/EcInput';
import EcModalCard from '~/components/Shared/EcModalCard/EcModalCard';
import ChevronRightIcon from '~/components/Shared/Icons/ChevronRightIcon';
import { Button } from '~/eds/src/components/Button/Button';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { ClientIdType } from '~/enums/ClientIdType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { UserRoleType } from '~/enums/UserRoleType';
import { useIntl } from 'react-intl';
import { canUseWorkflow } from '~/permissions/canUseWorkflow';
import { checkIsInternalSuperAdmin } from '~/permissions/checkIsInternalSuperAdmin';
import { MODAL_DEPARTMENT_TREE } from '~/types/modal.types';
import Checkbox from '~/ui/components/Checkbox/Checkbox';
import SingleSelect from '~/ui/components/SingleSelect/SingleSelect';
import { isEmailValid } from '~/utils/helper.utils';
import { featureFlagIncluded, testIsSuperAdmin } from '~/utils/user';

import { styles } from './AdminUserModal.styles';

function useStateFromProp(initialValue) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => setValue(initialValue), [initialValue]);

  return [value, setValue];
}

const AdminUserModal = (props) => {
  const [formFields, handleInputChange] = useStateFromProp(props.formFields);
  const intl = useIntl();

  const roleOptions = [
    {
      value: UserRoleType.Admin,
      label: intl.formatMessage({
        id: 'AdminUserModal.admin',
        defaultMessage: 'Admin',
        description: 'Label for the Admin role in Admin User Modal',
      }),
    },
    {
      value: UserRoleType.User,
      label: intl.formatMessage({
        id: 'AdminUserModal.user',
        defaultMessage: 'User',
        description: 'Label for the User role in Admin User Modal',
      }),
    },
    {
      value: UserRoleType.ReadOnly,
      label: intl.formatMessage({
        id: 'AdminUserModal.readOnlyUser',
        defaultMessage: 'Read Only User',
        description: 'Label for the Read Only User role in Admin User Modal',
      }),
    },
    {
      value: UserRoleType.PowerUserWithEdit,
      label: intl.formatMessage({
        id: 'AdminUserModal.powerUser',
        defaultMessage: 'Power User',
        description: 'Label for the Power User role in Admin User Modal',
      }),
    },
    {
      value: UserRoleType.WorkflowOnly,
      label: intl.formatMessage({
        id: 'AdminUserModal.workflowOnlyUser',
        defaultMessage: 'Workflow Only User',
        description:
          'Label for the Workflow Only User role in Admin User Modal',
      }),
    },
  ];

  const superAdminRoleOptions = [
    ...roleOptions,
    {
      value: UserRoleType.Reviewer,
      label: intl.formatMessage({
        id: 'AdminUserModal.reviewer',
        defaultMessage: 'Reviewer',
        description: 'Label for the Reviewer role in Admin User Modal',
      }),
    },
  ];

  const {
    classes,
    clientId,
    forEditing,
    title,
    confirmButtonText,
    confirmButtonDisabled,
    hideModal,
    errorMessage,
    currentUser,
    handleShowSecondaryModal,
    onUserFormUpdate,
  } = props;

  const isGeneralClient = Number.parseInt(clientId) === ClientIdType.General;
  const options =
    checkIsInternalSuperAdmin(currentUser) && isGeneralClient
      ? superAdminRoleOptions
      : roleOptions;

  function handleFormFieldChange(name, value) {
    const shouldEnableAllAdmin =
      name === 'role' && value === UserRoleType.Admin;
    const shouldDisableAllAdmin =
      name === 'role' &&
      [UserRoleType.WorkflowOnly, UserRoleType.PowerUserWithEdit].includes(
        value,
      );
    const updatedAutomationAdmin =
      name === 'automationAdmin' ? value : formFields.automationAdmin;
    const updatedWorkflowAdmin =
      name === 'workflowAdmin' ? value : formFields.workflowAdmin;
    const automationAdmin =
      shouldEnableAllAdmin ||
      (!shouldDisableAllAdmin && updatedAutomationAdmin);
    const workflowAdmin =
      shouldEnableAllAdmin || (!shouldDisableAllAdmin && updatedWorkflowAdmin);

    handleInputChange({
      ...formFields,
      [name]: value,
      automationAdmin,
      workflowAdmin,
    });
    onUserFormUpdate({
      ...formFields,
      [name]: value,
      automationAdmin,
      workflowAdmin,
    });
  }

  function handleInputFocus(event) {
    event.target.select();
  }

  function handleActionClick() {
    const {
      email,
      firstName,
      lastName,
      role,
      password,
      jobTitle,
      workflowAdmin,
      automationAdmin,
      shouldSendEmail,
    } = formFields;
    const { handleNameChange } = props;

    handleNameChange(
      email.trim(),
      firstName.trim(),
      lastName.trim(),
      role,
      password,
      jobTitle,
      workflowAdmin,
      automationAdmin,
      shouldSendEmail,
    );
  }

  function renderPassword() {
    const isSuperAdmin = testIsSuperAdmin(currentUser);
    return forEditing || !isSuperAdmin ? null : (
      <div>
        <label htmlFor="AdminUserModal/password">
          {intl.formatMessage({
            id: 'AdminUserModal.initialPasswordLabel',
            defaultMessage:
              'INITIAL PASSWORD (Click dropdown next to user to send a password setup email)',
            description: 'Initial password field label',
          })}
        </label>
        <div className={classes.passwordHelp} data-tip data-for="helpTooltip">
          <Icon
            id="helpTooltip"
            size="s"
            icon="help"
            text="help"
            placement="right"
            tooltip={intl.formatMessage({
              id: 'AdminUserModal.passwordTooltip',
              defaultMessage:
                "Passwords must be a minimum of 15 characters, containing at least one capital letter (A-Z), at least one number (0-9), at least one of the following special characters (@ * _ - . ! & '), no more than 3 identical characters in a row, and no more than 3 consecutive characters in a sequence.",
              description: 'Password requirements tooltip',
            })}
          />
        </div>
        <EcInput
          id="AdminUserModal/password"
          input={{
            value: formFields.password,
            onChange: (event) => {
              handleFormFieldChange('password', event.target.value);
            },
            onFocus: (event) => {
              handleInputFocus(event);
            },
          }}
          error={errorMessage.includes('password')}
        />
      </div>
    );
  }

  function renderErrorMessage() {
    const { classes, errorMessage } = props;

    return errorMessage.length ? (
      <div className={classes.modalErrorMessage}>{errorMessage}</div>
    ) : null;
  }

  function renderJobTitle() {
    return (
      <div>
        <label htmlFor="AdminUserModal/jobTitle">
          {intl.formatMessage({
            id: 'AdminUserModal.jobTitleLabel',
            defaultMessage: 'JOB TITLE',
            description: 'Job title field label',
          })}
        </label>
        <EcInput
          id="AdminUserModal/jobTitle"
          input={{
            value: formFields.jobTitle,
            onChange: (event) => {
              handleFormFieldChange('jobTitle', event.target.value);
            },
            onFocus: (event) => {
              handleInputFocus(event);
            },
          }}
        />
      </div>
    );
  }

  const isFormInvalid =
    !formFields.firstName?.trim().length ||
    !formFields.lastName?.trim().length ||
    !isEmailValid(formFields.email.trim());

  return (
    <EcModalCard
      title={title}
      content={
        <div className={classes.modalBodyContent}>
          <label htmlFor="AdminUserModal/email">
            {intl.formatMessage({
              id: 'AdminUserModal.emailLabel',
              defaultMessage: 'EMAIL *',
              description: 'Email field label',
            })}
          </label>
          <EcInput
            id="AdminUserModal/email"
            input={{
              'data-testid': 'admin-user-modal-email-input',
              value: formFields.email,
              onChange: (event) => {
                handleFormFieldChange('email', event.target.value);
              },
              onFocus: (event) => {
                handleInputFocus(event);
              },
            }}
            error={errorMessage.includes('email')}
          />

          <label htmlFor="AdminUserModal/firstName">
            {intl.formatMessage({
              id: 'AdminUserModal.firstNameLabel',
              defaultMessage: 'FIRST NAME *',
              description: 'First name field label',
            })}
          </label>
          <EcInput
            id="AdminUserModal/firstName"
            input={{
              'data-testid': 'admin-user-modal-first-name-input',
              value: formFields.firstName,
              onChange: (event) => {
                handleFormFieldChange('firstName', event.target.value);
              },
              onFocus: (event) => {
                handleInputFocus(event);
              },
            }}
          />
          <label htmlFor="AdminUserModal/lastName">
            {intl.formatMessage({
              id: 'AdminUserModal.lastNameLabel',
              defaultMessage: 'LAST NAME *',
              description: 'Last name field label',
            })}
          </label>
          <EcInput
            id="AdminUserModal/lastName"
            input={{
              'data-testid': 'admin-user-modal-last-name-input',
              value: formFields.lastName,
              onChange: (event) => {
                handleFormFieldChange('lastName', event.target.value);
              },
              onFocus: (event) => {
                handleInputFocus(event);
              },
            }}
          />

          {renderPassword()}

          {renderJobTitle()}

          <div className={classes.accessCopy}>
            <div className={classes.subHeader}>
              {intl.formatMessage({
                id: 'AdminUserModal.accessLevelDepartmentHeader',
                defaultMessage: 'Access Level and Department',
                description: 'Access level and department section header',
              })}
            </div>
            <p>
              {intl.formatMessage({
                id: 'AdminUserModal.accessLevelDescription',
                defaultMessage:
                  'Assign a specific access level for the user you are adding to the platform. You can also assign them to a specific department for easier access control management.',
                description: 'Access level description',
              })}
            </p>
          </div>
          <label htmlFor="AdminUserModal/accessLevel">
            {intl.formatMessage({
              id: 'AdminUserModal.accessLevelLabel',
              defaultMessage: 'ACCESS LEVEL',
              description: 'Access level field label',
            })}
          </label>
          <SingleSelect
            id="AdminUserModal/accessLevel"
            blacklistValues={
              canUseWorkflow(currentUser) ? [] : [UserRoleType.WorkflowOnly]
            }
            options={options}
            value={formFields.role}
            width="fullWidth"
            isClearable={false}
            shouldHideSelectedOptions={false}
            onChange={(updatedValue) =>
              handleFormFieldChange('role', updatedValue)
            }
          />
          {featureFlagIncluded(currentUser, FeatureFlagType.Workflow) ? (
            <div className={classes.workflowAdminContainer}>
              <Checkbox
                disabled={formFields.role === UserRoleType.WorkflowOnly}
                label={intl.formatMessage({
                  id: 'AdminUserModal.workflowAdminLabel',
                  defaultMessage: 'Workflow Admin',
                  description: 'Workflow admin checkbox label',
                })}
                value={formFields.workflowAdmin}
                onChange={(value) =>
                  handleFormFieldChange('workflowAdmin', value)
                }
              />
            </div>
          ) : null}
          <div className={classes.workflowAdminContainer}>
            <Checkbox
              disabled={formFields.role === UserRoleType.WorkflowOnly}
              label={intl.formatMessage({
                id: 'AdminUserModal.automationAdminLabel',
                defaultMessage: 'Automation Admin',
                description: 'Automation admin checkbox label',
              })}
              value={formFields.automationAdmin}
              onChange={(value) =>
                handleFormFieldChange('automationAdmin', value)
              }
            />
          </div>
          <div className={classes.departmentContainer}>
            <div className={classes.departmentLabel}>
              {intl.formatMessage({
                id: 'AdminUserModal.departmentLabel',
                defaultMessage: 'DEPARTMENT',
                description: 'Department field label',
              })}
            </div>
            <div>
              {!formFields.departmentsSelected ||
              !formFields.departmentsSelected.length ? (
                <div className={classes.noDeptMessage}>
                  {intl.formatMessage({
                    id: 'AdminUserModal.noDepartmentSelected',
                    defaultMessage: 'No department has been selected.',
                    description: 'No department selected message',
                  })}
                </div>
              ) : (
                <div className={classes.selectedDepartmentName}>
                  {formFields.departmentsSelected
                    .map((d) => d.department_name)
                    .join(' • ')}
                </div>
              )}
              <button
                aria-haspopup="true"
                className={classes.selectDepartmentCTA}
                onClick={() => handleShowSecondaryModal(MODAL_DEPARTMENT_TREE)}
              >
                <ChevronRightIcon size="15" color={evisortBlue} />
                {intl.formatMessage({
                  id: 'AdminUserModal.selectDepartmentButton',
                  defaultMessage: 'Select Department',
                  description: 'Select department button text',
                })}
              </button>
            </div>
          </div>
          {renderErrorMessage()}
        </div>
      }
      footer={
        <>
          <Button
            variant="tertiary"
            text={intl.formatMessage({
              id: 'AdminUserModal.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            })}
            onClick={hideModal}
          />
          {!forEditing && (
            <Checkbox
              label={intl.formatMessage({
                id: 'AdminUserModal.emailUserSignInInfo',
                defaultMessage: 'Email user sign-in info',
                description: 'Email user sign-in info checkbox label',
              })}
              value={formFields.shouldSendEmail}
              onChange={(value) =>
                handleFormFieldChange('shouldSendEmail', value)
              }
            />
          )}

          <Button
            id="admin-user-modal-submit-button"
            label={confirmButtonText}
            variant="primary"
            text={confirmButtonText}
            onClick={handleActionClick}
            disabled={isFormInvalid || confirmButtonDisabled}
          />
        </>
      }
      hideModal={hideModal}
    />
  );
};

AdminUserModal.propTypes = {
  title: PropTypes.string.isRequired,
  confirmButtonIcon: PropTypes.object,
  confirmButtonText: PropTypes.string.isRequired,
  confirmButtonDisabled: PropTypes.bool,
  formFields: PropTypes.shape({
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    role: PropTypes.number,
    password: PropTypes.string,
    jobTitle: PropTypes.string,
    workflowAdmin: PropTypes.bool,
    automationAdmin: PropTypes.bool,
    shouldSendEmail: PropTypes.bool,
  }),
  errorMessage: PropTypes.string,
  hideModal: PropTypes.func.isRequired,
};

const mapStateToProps = ({ currentUser }) => ({ currentUser });

export default injectSheet(styles)(connect(mapStateToProps)(AdminUserModal));
